/**
 * TT-803 live closure — the bounded pick-lists, as a form uses them.
 *
 * Two surfaces name something outside their own table: `category_availability`
 * carries a `category_id` and `product_availability` carries a `product_id`.
 * Until now an operator had to *know* those ids and type them, which is how a
 * block gets authored against an id nothing matches — a control that looks
 * applied, is stored, is audited, and blocks nobody.
 *
 * `GET /commerce-control/options` answers "what may I name?" and this module
 * shapes that answer for a selector. Three rules it does not bend:
 *
 * 1. **Only what the server returned may be offered.** No id is synthesized
 *    here, no list is merged from a second source, and nothing is ranked — the
 *    server's stable `name, id` ordering is preserved so two operators looking
 *    at the same catalogue see the same list.
 * 2. **An already-authored id that is not in the list is shown, not hidden.**
 *    A product deactivated after a block was authored still has a stored row.
 *    Dropping it from the form would silently rewrite the operator's change on
 *    the next save; instead it is kept, marked, and explained. That is a
 *    selected *value*, never an offered *option*.
 * 3. **Truncation is stated.** The product page is capped server-side. When it
 *    truncates, the screen says to search rather than letting an operator
 *    conclude a product is gone.
 *
 * Pure — no React, no network, no clock read.
 */

/**
 * Mirrors `commerce_control_options.DEFAULT_PRODUCT_LIMIT` / `MAX_PRODUCT_LIMIT`
 * / `MAX_SEARCH_LEN`, and is pinned against that module by
 * `commerceControlLogic.contract.test.js` — a bound that drifted from the
 * route's own would turn an ordinary search into a 422.
 */
export const DEFAULT_PRODUCT_LIMIT = 100;
export const MAX_PRODUCT_LIMIT = 200;
export const MAX_SEARCH_LENGTH = 64;

export const CATALOG_UNAVAILABLE_MESSAGE =
  "The product catalogue could not be read, so no product can be picked from a " +
  "list here. Categories are unaffected. Nothing is blocked or unblocked by this.";

export const PRODUCTS_TRUNCATED_MESSAGE =
  "More products exist than this list shows. Search by name or id to find the " +
  "one you want — it is not missing.";

export const OPTIONS_UNLOADED_MESSAGE =
  "The category and product lists have not loaded, so this control cannot be " +
  "authored against a name yet. Try refreshing.";

export const UNKNOWN_CATEGORY_NOTE =
  "This category id is not in the current canonical list. It is what the stored " +
  "row already says — it is kept so saving does not silently change it.";

export const UNKNOWN_PRODUCT_NOTE =
  "This product id is not in the customer-visible catalogue right now. It is " +
  "what the stored row already says — it is kept so saving does not silently " +
  "change it. Search for it, or leave it as it is.";

/** Nothing loaded yet. Every field is a denial or an empty list, never a guess. */
export const EMPTY_OPTIONS = Object.freeze({
  loaded: false,
  categories: Object.freeze([]),
  products: Object.freeze([]),
  categoryCount: 0,
  productCount: 0,
  catalogAvailable: false,
  truncated: false,
  search: "",
  limit: DEFAULT_PRODUCT_LIMIT,
  maxLimit: MAX_PRODUCT_LIMIT,
  maxSearchLength: MAX_SEARCH_LENGTH,
});

function text(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * The `/options` document as a form uses it.
 *
 * Every list is filtered to entries that carry a usable id: an option a
 * selector cannot submit is worse than one it never offered, because it looks
 * choosable. Labels fall back to the id so a row with a blank name is still
 * pickable rather than an empty line.
 */
export function normalizeOptions(doc) {
  if (!doc || typeof doc !== "object") return EMPTY_OPTIONS;

  const categories = (Array.isArray(doc.categories) ? doc.categories : [])
    .map((entry) => ({
      id: text(entry?.category_id),
      label: text(entry?.label) || text(entry?.category_id),
    }))
    .filter((entry) => entry.id);

  const products = (Array.isArray(doc.products) ? doc.products : [])
    .map((entry) => ({
      id: text(entry?.product_id),
      label: text(entry?.name) || text(entry?.product_id),
      // The *resolved* canonical category, when the taxonomy knows this row's
      // raw category. `null` is a normal outcome and is shown as such — an
      // operator must not be told a product sits in a canonical lane that a
      // block authored against it would not actually match.
      categoryId: text(entry?.category_id) || null,
      categoryLabel: text(entry?.category_label) || null,
      rawCategory: text(entry?.category) || null,
    }))
    .filter((entry) => entry.id);

  return {
    loaded: true,
    categories,
    products,
    categoryCount: Number(doc.category_count) || categories.length,
    productCount: Number(doc.product_count) || products.length,
    catalogAvailable: Boolean(doc.catalog_available),
    truncated: Boolean(doc.truncated),
    search: text(doc.search),
    limit: Number(doc.limit) || DEFAULT_PRODUCT_LIMIT,
    maxLimit: Number(doc.max_limit) || MAX_PRODUCT_LIMIT,
    maxSearchLength: Number(doc.max_search_length) || EMPTY_OPTIONS.maxSearchLength,
  };
}

function findById(list, id) {
  const wanted = text(id);
  if (!wanted) return null;
  return (list || []).find((entry) => entry.id === wanted) || null;
}

export function categoryOption(options, id) {
  return findById(options?.categories, id);
}

export function productOption(options, id) {
  return findById(options?.products, id);
}

/** Display label for a category id, falling back to the raw id. */
export function categoryLabel(options, id) {
  return categoryOption(options, id)?.label || text(id);
}

/**
 * Is this already-stored id absent from the list the server offered?
 *
 * Answers `false` while the options have not loaded: "we have not asked yet"
 * must never render as "that id is wrong".
 */
export function isUnknownCategory(options, id) {
  if (!options?.loaded || !text(id)) return false;
  return !categoryOption(options, id);
}

export function isUnknownProduct(options, id) {
  if (!options?.loaded || !options.catalogAvailable || !text(id)) return false;
  return !productOption(options, id);
}

/**
 * Narrow an already-bounded list by what the operator typed.
 *
 * Substring, case-insensitive, over the label and the id. This is a *local*
 * convenience on top of the page the server already sent — for products the
 * server's own `search` is what actually reaches past the cap, and
 * `PRODUCTS_TRUNCATED_MESSAGE` is what tells the operator so.
 */
export function filterOptions(list, term) {
  const wanted = text(term).toLowerCase();
  const entries = Array.isArray(list) ? list : [];
  if (!wanted) return entries;
  return entries.filter(
    (entry) =>
      entry.label.toLowerCase().includes(wanted) || entry.id.toLowerCase().includes(wanted)
  );
}

/**
 * The one sentence to show under a picker, or `""` when there is nothing to say.
 *
 * Ordered by what stops the operator first: no list at all, then a catalogue
 * that could not be read, then a list that is complete but capped.
 */
export function optionsNotice(options, kind) {
  if (!options?.loaded) return OPTIONS_UNLOADED_MESSAGE;
  if (kind === "product") {
    if (!options.catalogAvailable) return CATALOG_UNAVAILABLE_MESSAGE;
    if (options.truncated) return PRODUCTS_TRUNCATED_MESSAGE;
  }
  return "";
}

/** Bounded selectable entries for a category multi-toggle, in taxonomy order. */
export function categoryToggles(options, selectedIds) {
  const selected = new Set((Array.isArray(selectedIds) ? selectedIds : []).map(text));
  const offered = (options?.categories || []).map((entry) => ({
    ...entry,
    selected: selected.has(entry.id),
    known: true,
  }));
  // Ids the stored row already carries that the taxonomy no longer offers.
  // Listed after the canonical ones and still un-toggleable-off — the operator
  // must be able to remove one, which means it has to be rendered.
  //
  // `known` is decided by `isUnknownCategory`, not by "is it in the list I
  // happen to be holding": before the options load *every* stored id is absent
  // from an empty list, and marking them all as outside the canonical taxonomy
  // would be a false accusation against the operator's own stored row.
  const offeredIds = new Set(offered.map((entry) => entry.id));
  const strays = [...selected]
    .filter((id) => id && !offeredIds.has(id))
    .map((id) => ({ id, label: id, selected: true, known: !isUnknownCategory(options, id) }));
  return [...offered, ...strays];
}
